import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import hljs from 'highlight.js/lib/core';
import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import diff from 'highlight.js/lib/languages/diff';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';

import './AiOverviewPanel.css';
import { isMermaidErrorMarkup, MermaidDiagram } from '../MermaidDiagram';

hljs.registerLanguage('bash', bash);
hljs.registerLanguage('css', css);
hljs.registerLanguage('diff', diff);
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('json', json);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('xml', xml);

const remarkPlugins = [remarkGfm];
const rehypePlugins = [rehypeHighlight];
const FENCE_RE = /```([A-Za-z0-9_-]+)?[^\n]*\n([\s\S]*?)```/g;
const DEVELOPER_HIDDEN_MARKER_RE = /<!--8080-dev-[\s\S]*?-->/g;
const DEVELOPER_CODEGRAPH_SNAPSHOT_RE = /(?:^|\n)CodeGraphContext graph snapshot:\s*\n```svg\n[\s\S]*?```\s*/i;
const DIFF_STAT_RE = /([+\-−]\d+)/g;
const DIFF_STAT_PART_RE = /^[+\-−]\d+$/;
const INLINE_CODE_FILE_REFERENCE_RE = /^(?:\.{1,2}\/)?[\w@~.-]+(?:\/[\w@~.-]+)*\.(?:json|ts|tsx|js|jsx|mjs|cjs|css|scss|html|svg|md|py|go|rs|java|yml|yaml|toml|lock|env)$/i;

type DiagramKind = 'mermaid' | 'svg';

interface RichDiagramItem {
  kind: DiagramKind;
  title: string;
  content: string;
}

interface RichMarkdownProps {
  content: string;
  className?: string;
}

type CodeRendererProps = React.ComponentProps<'code'> & {
  inline?: boolean;
  node?: unknown;
  'data-rich-code-block'?: boolean;
};

const stripDeveloperMarkers = (content: string): string => (
  content
    .replace(DEVELOPER_CODEGRAPH_SNAPSHOT_RE, '\n')
    .replace(DEVELOPER_HIDDEN_MARKER_RE, '')
    .trimEnd()
);

const sanitizeInlineSvg = (raw: string): string => {
  const trimmed = raw.trim();

  if (!/^<svg[\s>]/i.test(trimmed) || trimmed.length > 1_000_000) return '';
  if (isMermaidErrorMarkup(trimmed)) return '';

  return trimmed
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\s(?:href|xlink:href)\s*=\s*(["'])\s*javascript:[\s\S]*?\1/gi, '');
};

const diagramTitle = (kind: DiagramKind, count: number): string => (
  `${kind === 'mermaid' ? 'Diagram' : 'CodeGraphContext SVG'}${count > 1 ? ` ${count}` : ''}`
);

const extractDiagramItems = (content: string): RichDiagramItem[] => {
  const items: RichDiagramItem[] = [];
  let match: RegExpExecArray | null;

  FENCE_RE.lastIndex = 0;

  while ((match = FENCE_RE.exec(content)) !== null) {
    const lang = (match[1] || '').toLowerCase();

    if (lang !== 'mermaid' && lang !== 'svg') continue;
    items.push({
      kind: lang,
      title: diagramTitle(lang, items.filter((item) => item.kind === lang).length + 1),
      content: (match[2] || '').trim(),
    });
  }

  return items;
};

const diagramSignature = (kind: DiagramKind, content: string): string => `${kind}:${content.trim()}`;

const isDiagramKind = (language: string): language is DiagramKind => language === 'mermaid' || language === 'svg';

const languageFromClassName = (className?: string): string => (
  /language-([A-Za-z0-9_-]+)/.exec(className || '')?.[1]?.toLowerCase() || ''
);

const escapeHtml = (value: string): string => (
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
);

const nodeText = (children?: React.ReactNode): string => (
  React.Children.toArray(children)
    .map((child) => {
      if (child === null || child === undefined || typeof child === 'boolean') return '';
      if (typeof child === 'string' || typeof child === 'number') return String(child);
      if (Array.isArray(child)) return nodeText(child);
      if (React.isValidElement<{ children?: React.ReactNode }>(child)) return nodeText(child.props.children);

      return '';
    })
    .join('')
    .replace(/\n$/, '')
);

const inferInlineCodeLanguage = (value: string): string | undefined => {
  if (!value) return undefined;
  if (INLINE_CODE_FILE_REFERENCE_RE.test(value) || value.includes('/') || value.includes('\\')) return undefined;
  if (/^\{[\s\S]*\}$|^\[[\s\S]*\]$/.test(value) || /^"(?:\\[\s\S]|[^"\\])*"\s*:/.test(value)) return 'json';
  if (/^(?:npm|pnpm|yarn|node|git|docker|curl|cd|mkdir|rm|cp|mv)\b/.test(value)) return 'bash';
  if (/^\$\([^)]+\)$/.test(value)) return undefined;
  if (/^(?:["'`])/.test(value)
    || /\b(?:abstract|async|await|class|const|declare|export|extends|function|import|interface|let|new|private|protected|public|readonly|return|type|var)\b/.test(value)
    || /[=<>!&|?:]/.test(value)
    || /[A-Za-z_$][\w$]*\s*\(/.test(value)
    || /[A-Za-z_$][\w$]*\.[A-Za-z_$][\w$]*/.test(value)
    || /^[A-Z][A-Za-z0-9_]*(?:<.*>)?$/.test(value)) {
    return 'typescript';
  }

  return undefined;
};

const highlightInlineCode = (value: string): string => {
  if (!value) return '';
  const language = inferInlineCodeLanguage(value.trim());
  if (!language) return escapeHtml(value);

  return hljs.highlight(value, { language, ignoreIllegals: true }).value;
};

const renderDiffStatText = (value: string): React.ReactNode[] => (
  value.split(DIFF_STAT_RE).map((part, index) => {
    if (!part) return null;
    if (!DIFF_STAT_PART_RE.test(part)) return part;

    const kind = part.startsWith('+') ? 'add' : 'remove';

    return (
      <span key={`${part}-${index}`} className={`rich-diff-stat rich-diff-stat--${kind}`}>
        {part}
      </span>
    );
  }).filter(Boolean) as React.ReactNode[]
);

const renderDiffStatChildren = (children: React.ReactNode): React.ReactNode => (
  React.Children.map(children, (child) => (typeof child === 'string' ? renderDiffStatText(child) : child))
);

const diagramIndexFor = (
  items: RichDiagramItem[],
  renderCounts: Map<string, number>,
  kind: DiagramKind,
  code: string,
): number => {
  const signature = diagramSignature(kind, code);
  const occurrence = renderCounts.get(signature) ?? 0;

  renderCounts.set(signature, occurrence + 1);
  let seen = 0;

  return items.findIndex((item) => {
    if (diagramSignature(item.kind, item.content) !== signature) return false;
    const matches = seen === occurrence;

    seen += 1;

    return matches;
  });
};

const DiagramBlock: React.FC<{
  item: RichDiagramItem;
  onOpen: () => void;
}> = ({ item, onOpen }) => (
  <div className={`rich-diagram-block rich-diagram-block--${item.kind}`}>
    <div className="rich-diagram-block__header">
      <span>{item.title}</span>
      <button type="button" className="rich-diagram-block__fullscreen" onClick={onOpen}>
        Full screen
      </button>
    </div>
    <div className="rich-diagram-block__stage">
      {item.kind === 'mermaid' ? (
        <MermaidDiagram content={item.content} id={`rich-${item.kind}-${item.content.length}`} skipAnimation />
      ) : (
        <div
          className="rich-diagram-block__svg"
          dangerouslySetInnerHTML={{ __html: sanitizeInlineSvg(item.content) || '<pre>Invalid SVG diagram</pre>' }}
        />
      )}
    </div>
  </div>
);

const DiagramViewerModal: React.FC<{
  items: RichDiagramItem[];
  initialIndex: number;
  onClose: () => void;
}> = ({ items, initialIndex, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const item = items[index] || items[0];

  if (!item) return null;

  const go = (delta: number) => {
    setScale(1);
    setIndex((current) => (current + delta + items.length) % items.length);
  };

  return (
    <div className="rich-diagram-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="rich-diagram-modal__panel" onClick={(event) => event.stopPropagation()}>
        <div className="rich-diagram-modal__bar">
          <div className="rich-diagram-modal__title">{item.title}</div>
          <div className="rich-diagram-modal__controls">
            <button type="button" onClick={() => go(-1)} disabled={items.length <= 1}>Prev</button>
            <span>{index + 1} / {items.length}</span>
            <button type="button" onClick={() => go(1)} disabled={items.length <= 1}>Next</button>
            <button type="button" onClick={() => setScale((value) => Math.max(0.35, value - 0.15))}>-</button>
            <button type="button" onClick={() => setScale(1)}>{Math.round(scale * 100)}%</button>
            <button type="button" onClick={() => setScale((value) => Math.min(3, value + 0.15))}>+</button>
          </div>
          <button type="button" className="rich-diagram-modal__close" onClick={onClose}>Close</button>
        </div>
        <div className="rich-diagram-modal__viewport">
          <div className="rich-diagram-modal__canvas" style={{ transform: `scale(${scale})` }}>
            {item.kind === 'mermaid' ? (
              <MermaidDiagram content={item.content} id={`rich-modal-${index}-${item.content.length}`} skipAnimation />
            ) : (
              <div
                className="rich-diagram-modal__svg"
                dangerouslySetInnerHTML={{ __html: sanitizeInlineSvg(item.content) || '<pre>Invalid SVG diagram</pre>' }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const diagramItemFor = (
  items: RichDiagramItem[],
  renderCounts: Map<string, number>,
  kind: DiagramKind,
  code: string,
): { item: RichDiagramItem; index: number } => {
  const index = diagramIndexFor(items, renderCounts, kind, code);
  const item = items[index] || { kind, title: diagramTitle(kind, 1), content: code };

  return { item, index };
};

const createMarkdownComponents = (
  items: RichDiagramItem[],
  onOpen: (item: RichDiagramItem, index: number) => void,
): Components => {
  const renderCounts = new Map<string, number>();

  return {
    pre({ children, ...props }) {
      const childArray = React.Children.toArray(children);
      const hasDiagramCode = childArray.some((child) => (
        React.isValidElement<{ className?: string }>(child)
          && isDiagramKind(languageFromClassName(child.props.className))
      ));

      if (hasDiagramCode) return <>{children}</>;

      return (
        <pre {...props}>
          {React.Children.map(children, (child) => (
            React.isValidElement<CodeRendererProps>(child)
              ? React.cloneElement(child, { 'data-rich-code-block': true })
              : child
          ))}
        </pre>
      );
    },
    code({
      inline,
      node: _node,
      className: codeClassName,
      children,
      'data-rich-code-block': isPreCode,
      ...props
    }: CodeRendererProps) {
      const language = languageFromClassName(codeClassName);
      const code = nodeText(children);
      const isInlineCode = typeof inline === 'boolean' ? inline : !isPreCode && !language && !code.includes('\n');

      if (!isInlineCode && isDiagramKind(language)) {
        const { item, index } = diagramItemFor(items, renderCounts, language, code);

        return <DiagramBlock item={item} onOpen={() => onOpen(item, index)} />;
      }

      if (isInlineCode) {
        return (
          <code
            className="rich-inline-code hljs"
            dangerouslySetInnerHTML={{ __html: highlightInlineCode(code) }}
            {...props}
          />
        );
      }

      return <code className={codeClassName} {...props}>{children}</code>;
    },
    p({ children, ...props }) {
      return <p {...props}>{renderDiffStatChildren(children)}</p>;
    },
    li({ children, ...props }) {
      return <li {...props}>{renderDiffStatChildren(children)}</li>;
    },
  };
};

export const RichMarkdown: React.FC<RichMarkdownProps> = ({ content, className }) => {
  const displayContent = useMemo(() => stripDeveloperMarkers(content), [content]);
  const diagramItems = useMemo(() => extractDiagramItems(displayContent), [displayContent]);
  const [viewer, setViewer] = useState<{ items: RichDiagramItem[]; index: number } | null>(null);
  const components = createMarkdownComponents(diagramItems, (item, index) => (
    setViewer({ items: diagramItems.length ? diagramItems : [item], index: Math.max(index, 0) })
  ));
  const rootClassName = ['rich-markdown-content', className].filter(Boolean).join(' ');

  return (
    <div className={rootClassName}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {displayContent}
      </ReactMarkdown>
      {viewer && createPortal(
        <DiagramViewerModal
          items={viewer.items}
          initialIndex={viewer.index}
          onClose={() => setViewer(null)}
        />,
        document.body,
      )}
    </div>
  );
};

export default RichMarkdown;
